/**
 * Keeper Duel API helpers. Sudden-death duel flow: start today's session, then report the finished
 * run. The server owns the economy — coins depend only on the tier table + daily cap + per-runId
 * idempotency, never on a client-supplied amount.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { KEEPER_DUEL_GAME_KEY } from '../constants/keeperDuel.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface KeeperDuelStart {
  dailyCoinCap: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestStreak: number;
}

export interface KeeperDuelFinishResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestStreak: number;
  newBest: boolean;
}

export async function startKeeperDuelRun(apiClient: ApiClient): Promise<KeeperDuelStart> {
  const response = await apiClient<{ success: boolean; data: KeeperDuelStart; message?: string }>(
    API_ENDPOINTS.games.duelStart(KEEPER_DUEL_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function finishKeeperDuelRun(
  apiClient: ApiClient,
  args: { runId: string; streak: number },
): Promise<KeeperDuelFinishResult> {
  const response = await apiClient<{ success: boolean; data: KeeperDuelFinishResult; message?: string }>(
    API_ENDPOINTS.games.duelFinish(KEEPER_DUEL_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the run');
  return payload.data;
}
